/**
 * useCharModel — the one place the frontend reads the staged CHAR model.
 *
 * Everything under src/assets/explorer/{graph,registries,controls,requirements,
 * process-domain} is written by the framework repo's
 * tools/stage_explorer_content.py (make deploy-explorer-write). Nothing here is
 * authored in this repo; if a number or a mapping is wrong, fix the framework
 * source and redeploy. This module only types, indexes and joins that content.
 */

import graphJson from '@/assets/explorer/graph/framework-graph-data.json';
import catalogJson from '@/assets/explorer/controls/ashc_controls_catalog.json';
import registryJson from '@/assets/explorer/registries/ashmatics-framework-registry.json';
import type { FrameworkGraphData } from '@/types/blueprint';

// ── Controls catalog ─────────────────────────────────────────────────────

export type MaturityLevel = 'performed' | 'managed' | 'established' | 'predictable' | 'optimizing';
export const MATURITY_LEVELS: MaturityLevel[] = ['performed', 'managed', 'established', 'predictable', 'optimizing'];

export interface AshcEvidence {
  level: MaturityLevel;
  text: string;
  mechanisms?: string[];
}

export interface AshcControl {
  control_id: string;
  title: string;
  family: string;
  description: string;
  legacy_ids?: string[];
  char_bindings?: {
    process_domains?: string[];
    base_practices?: string[];
    domain_label?: string;
  };
  inputs?: string[];
  outputs?: string[];
  tooling?: string[];
  foundational?: boolean;
  applicability?: { engagement_scope?: string; condition?: string; rationale?: string };
  depends_on?: string[];
  maturity?: {
    minimum?: MaturityLevel;
    levels?: Partial<Record<MaturityLevel, string>>;
    evidence?: AshcEvidence[];
  };
  assurance?: { minimum?: string; cadence?: { default?: string; [k: string]: unknown } };
  derived_from?: { iso_42001_clauses?: string[]; [k: string]: unknown };
  note?: string;
}

export interface AshcFamily {
  name: string;
  description: string;
  control_count: number;
}

interface AshcCatalog {
  catalog_metadata: { version: string; total_controls: number; total_families: number; updated?: string };
  families: Record<string, AshcFamily>;
  controls: AshcControl[];
}

// ── Framework registry (the slice the explorer reads) ────────────────────

export interface RegistryPolicyDomain {
  name: string;
  artifactId: string;
  tier: number;
  category: string;
  description: string;
  status?: string;
  appliesToProcessDomains: string[];
  iso42001Clauses?: string[];
  assuranceControls?: string[];
  assuranceControlTitles?: Record<string, string>;
  assuranceMappingRationale?: string;
  regulatoryMapping?: Record<string, string[]>;
}

export interface RegistryProcessDomain {
  name: string;
  areaId: string;
  category: string;
  description: string;
  basePractices: string[];
  policyBindings: string[];
  upstreamDomains: string[];
  downstreamDomains: string[];
  sopCount: number;
}

export interface RegistryRegulatoryFramework {
  name: string;
  crosswalkFile?: string;
  wizardLogicFile?: string;
  coverageEstimate?: string;
  [k: string]: unknown;
}

interface FrameworkRegistry {
  frameworkMetadata: { name: string; shortName: string; version: string; lastUpdated: string };
  policyDomains: Record<string, RegistryPolicyDomain>;
  processDomains: Record<string, RegistryProcessDomain>;
  regulatoryFrameworks: Record<string, RegistryRegulatoryFramework>;
}

// ── Crosswalks + requirement layers ──────────────────────────────────────

export interface CrosswalkMapping {
  control_id: string;
  addresses: string[];
  requirements?: string[];
  legacy_ids?: string[];
  status?: string;
  note?: string;
}

export interface Crosswalk {
  crosswalk_metadata: { crosswalk_id: string; framework_name: string; framework_version?: string; version?: string; status?: string };
  mappings: CrosswalkMapping[];
}

/** One external framework, as the explorer presents it. */
export interface FrameworkRef {
  /** key used in code, e.g. 'iso-42001' */
  id: string;
  /** short display label, e.g. 'ISO 42001' */
  label: string;
  crosswalk: Crosswalk;
  /** number of requirements the requirement layer declares, when known */
  totalRequirements: number | null;
}

// Vite resolves these globs at build time; eager so the model is synchronous.
const crosswalkModules = import.meta.glob('@/assets/explorer/controls/crosswalks/*.json', { eager: true }) as Record<string, { default: Crosswalk }>;
const requirementModules = import.meta.glob('@/assets/explorer/requirements/*.json', { eager: true }) as Record<string, { default: Record<string, unknown> }>;
const processDomainModules = import.meta.glob('@/assets/explorer/process-domain/*-process-domain.json', { eager: true }) as Record<string, { default: ProcessDomainFile }>;

// crosswalk file stem -> (label, requirement-layer file stem)
const FRAMEWORKS: Record<string, { label: string; layer: string }> = {
  'iso-42001':       { label: 'ISO 42001',     layer: 'iso-42001-logic' },
  'nist-ai-rmf':     { label: 'NIST AI RMF',   layer: 'nist-rmf-logic' },
  'jc-ruaih':        { label: 'JC RUAIH',      layer: 'jc-ruaih-logic' },
  'arch-ai':         { label: 'ACR ARCH-AI',   layer: 'arch-ai-mapping' },
  'onc-hti1':        { label: 'ONC HTI-1',     layer: 'onc-hti1-logic' },
  'colorado-ai-act': { label: 'Colorado AI Act', layer: 'colorado-ai-act-logic' },
  'aca-1557':        { label: 'ACA §1557',     layer: 'aca-section-1557-logic' },
};

function stem(path: string): string {
  return path.split('/').pop()!.replace(/\.json$/, '');
}

/**
 * Count the requirement identities a requirement layer declares — the same
 * containers the KB seeder reads (requirements[], plus nested subcategories /
 * nist_subcategories / sub_requirements / items). Null when the layer has no
 * top-level requirements list.
 */
function countRequirements(layer: Record<string, unknown> | undefined): number | null {
  const reqs = layer?.requirements;
  if (!Array.isArray(reqs)) return null;
  const NESTED = ['subcategories', 'nist_subcategories', 'sub_requirements', 'items'];
  let n = 0;
  for (const r of reqs as Record<string, unknown>[]) {
    n += 1;
    for (const k of NESTED) {
      const sub = r[k];
      if (Array.isArray(sub)) n += sub.length;
    }
  }
  return n;
}

// ── Process domain files (BPs, SOPs, outputs) ────────────────────────────

export interface ProcessDomainBasePractice {
  practice_id: string;
  name: string;
  description: string;
  order: number;
  work_products?: string[];
  tooling_references?: string[];
  policy_tokens?: string[];
}

export interface ProcessDomainSop {
  sop_id: string;
  name: string;
  description: string;
  linked_practices: string[];
  duration?: string;
  team_size?: string;
  primary_roles?: string[];
  inputs?: string[];
  outputs?: string[];
  process_output?: string;
  dependencies?: { sequential?: string[]; parallel?: string[]; feeds_to?: string[] };
}

export interface ProcessDomainFile {
  domain_metadata: { process_id: string; area_id?: string; title: string; category?: string; description?: string };
  base_practices: ProcessDomainBasePractice[];
  standard_operating_procedures: ProcessDomainSop[];
  process_outputs: { name: string; composed_from?: string[] }[];
  cross_domain_dependencies?: {
    upstream_dependencies?: { domain: string; dependency: string; practices?: string[] }[];
    downstream_dependencies?: { domain: string; dependency: string; practices?: string[] }[];
  };
}

// ── Indexes (built once at module load) ──────────────────────────────────

const graph = graphJson as unknown as FrameworkGraphData;
const catalog = catalogJson as unknown as AshcCatalog;
const registry = registryJson as unknown as FrameworkRegistry;

const controlsById = new Map<string, AshcControl>();
const controlsByLegacyId = new Map<string, AshcControl>();
for (const c of catalog.controls) {
  controlsById.set(c.control_id, c);
  for (const legacy of c.legacy_ids || []) controlsByLegacyId.set(legacy, c);
}

const requirementLayers: Record<string, Record<string, unknown>> = {};
for (const [path, mod] of Object.entries(requirementModules)) requirementLayers[stem(path)] = mod.default;

const frameworks: FrameworkRef[] = [];
for (const [path, mod] of Object.entries(crosswalkModules)) {
  const id = stem(path);
  const meta = FRAMEWORKS[id];
  frameworks.push({
    id,
    label: meta?.label ?? mod.default.crosswalk_metadata.framework_name,
    crosswalk: mod.default,
    totalRequirements: countRequirements(meta ? requirementLayers[meta.layer] : undefined),
  });
}
// stable presentation order: the FRAMEWORKS table order, unknowns last
const order = Object.keys(FRAMEWORKS);
frameworks.sort((a, b) => (order.indexOf(a.id) + 1 || 99) - (order.indexOf(b.id) + 1 || 99));

// control id -> framework id -> requirement ids addressed
const addressedByControl = new Map<string, Record<string, string[]>>();
for (const fw of frameworks) {
  for (const m of fw.crosswalk.mappings) {
    const byFw = addressedByControl.get(m.control_id) ?? {};
    byFw[fw.id] = [...new Set([...(byFw[fw.id] ?? []), ...(m.addresses ?? [])])];
    addressedByControl.set(m.control_id, byFw);
  }
}

const processDomainFiles: Record<string, ProcessDomainFile> = {};
for (const mod of Object.values(processDomainModules)) {
  processDomainFiles[mod.default.domain_metadata.process_id] = mod.default;
}

// ── Public API ───────────────────────────────────────────────────────────

/** Resolve a control by its ASHC id, or by a retired EXC-* id via legacy_ids. */
export function getControl(id: string): AshcControl | undefined {
  return controlsById.get(id) ?? controlsByLegacyId.get(id);
}

export function getControls(ids: string[] | undefined | null): AshcControl[] {
  return (ids ?? []).map(getControl).filter((c): c is AshcControl => !!c);
}

/** The ASHC controls a policy domain binds, per the framework registry. */
export function controlsForPolicy(code: string | undefined | null): AshcControl[] {
  if (!code) return [];
  return getControls(registry.policyDomains[code]?.assuranceControls);
}

/** framework id -> requirement ids this control addresses, across all crosswalks. */
export function requirementsAddressedBy(controlId: string): Record<string, string[]> {
  return addressedByControl.get(controlId) ?? {};
}

export function toolLabel(token: string): string {
  return graph.toolLabels?.[token] ?? token.replace(/^\{\{\s*tool\.|\s*\}\}$/g, '').replace(/^(tool|module):\/\//, '');
}

/** Headline counts, computed from the staged model so captions cannot go stale. */
export const modelStats = {
  frameworkVersion: registry.frameworkMetadata.version,
  capabilityDomains: graph.capabilityDomains.length,
  policyDomains: Object.keys(graph.capabilityAreas).length,
  processDomains: Object.keys(graph.processAreas).length,
  basePractices: Object.values(graph.processAreas).reduce((n, p) => n + p.bpCount, 0),
  sops: Object.values(graph.processAreas).reduce((n, p) => n + p.sopCount, 0),
  controls: catalog.catalog_metadata.total_controls,
  controlFamilies: catalog.catalog_metadata.total_families,
  frameworks: frameworks.length,
};

export function useCharModel() {
  return {
    graph,
    catalog,
    registry,
    families: catalog.families,
    controls: catalog.controls,
    policyDomains: registry.policyDomains,
    processDomains: registry.processDomains,
    frameworks,
    requirementLayers,
    processDomainFiles,
    modelStats,
    getControl,
    getControls,
    controlsForPolicy,
    requirementsAddressedBy,
    toolLabel,
  };
}
